import React, { useState, useEffect } from 'react';
import Shared from 'screens/shared';
import { useSelector } from 'react-redux';
import { actions, ACTIONS_TYPES } from 'redux/local';
import { VALIDATION } from 'constant';
import { _t, cleanEmptyProperty, sleep } from 'utils';
import { featuresToggle } from 'utils/features-toggle';
import DirectionArrow from './direction-arrow';
import templates from './templates.json';

export default ({
  isCancel = false, onCancel, properties = {}, dcId, serverId, nicId,
}) => {
  const [selectedProtocol, setSelectedProtocol] = useState('');
  const [rules, setRules] = useState(null);

  const { [ACTIONS_TYPES.DATA_CENTER.GET]: datacenter = {} } = useSelector((e) => e);
  const isVnfWf = featuresToggle.isExistInDatacenter(datacenter, 'vnf-fw');
  const isVnfCidr = featuresToggle.isExistInDatacenter(datacenter, 'vnf-cidr');

  const directionOptions = isVnfWf ? [
    ['INGRESS', <DirectionArrow type="INGRESS" />],
    ['EGRESS', <DirectionArrow type="EGRESS" />],
    // ['BIDIRECTIONAL', <DirectionArrow type="BIDIRECTIONAL" />],
  ] : [
    ['INGRESS', <DirectionArrow type="INGRESS" />],
  ];

  const { id } = properties;

  const model = {
    name: '',
    type: directionOptions[0][0],
    protocol: '',
    sourceMac: '',
    sourceIp: '',
    targetIp: '',
    portRangeStart: '',
    portRangeEnd: '',
    icmpType: '',
    icmpCode: '',
  };

  const [fieldsValues] = useState({
    ...model,
    ...Object.assign(model, properties),
  });

  const templateClickHandler = (e) => {
    setRules([...e]);
  };

  const fields = [
    {
      name: 'templateLabels',
      type: 'label',
      title: (
        <>
          <span className="primary-text">Pre-Defined Templates</span>
          { /* eslint-disable-next-line max-len */ }
          <Shared.widgets.Tiles titleClassName="-f-13" items={templates.map(({ name, rules: e }) => ({ name, icon: 'fire-alt', onClick: () => templateClickHandler(e) }))} />
        </>
      ),
    },
    {
      name: 'createTemplateLabel',
      type: 'label',
      title: (<span className="primary-text block m-t-10">Create Template</span>),
    },
    {
      name: 'name',
      type: 'text',
      title: _t('Name'),
    },
    {
      name: 'type',
      title: 'Direction',
      type: 'dropdown',
      options: [['INGRESS', <DirectionArrow type="INGRESS" />]],
    },
    {
      name: 'protocol',
      title: 'Protocol',
      type: 'dropdown',
      onChange: (value) => setSelectedProtocol(value),
      options: ['TCP', 'UDP', 'ICMP', 'ANY'],
    },
    {
      name: 'sourceMac',
      type: 'text',
      title: 'Source MAC',
    },
    {
      name: 'sourceIp',
      type: 'ip',
      title: 'Source IP',
    },
    {
      name: 'targetIp',
      type: 'ip',
      title: 'Target IP',
    },
    {
      name: 'portRangeStart',
      type: 'text',
      title: 'Port Range Start',
      visible: false,
    },
    {
      name: 'portRangeEnd',
      type: 'text',
      title: 'Port Range End',
      visible: false,
    },
    {
      name: 'icmpType',
      type: 'text',
      title: 'ICMP Type',
      visible: false,
    },
    {
      name: 'icmpCode',
      type: 'text',
      title: 'ICMP Code',
      visible: false,
    },
  ];

  const changeFields = [
    { name: 'icmpType', props: { visible: selectedProtocol === 'ICMP' || selectedProtocol === 'ANY' } },
    { name: 'icmpCode', props: { visible: selectedProtocol === 'ICMP' || selectedProtocol === 'ANY' } },
    { name: 'portRangeStart', props: { visible: selectedProtocol === 'TCP' || selectedProtocol === 'UDP' } },
    { name: 'portRangeEnd', props: { visible: selectedProtocol === 'TCP' || selectedProtocol === 'UDP' } },
    { name: 'type', props: { options: directionOptions, disabled: !isVnfWf } },
  ];

  const validations = [
    {
      name: 'name',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'name',
      type: VALIDATION.STRING,
      min: 3,
    },
    {
      name: 'protocol',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'sourceIp',
      type: isVnfCidr ? VALIDATION.IPS_CIDR : VALIDATION.IP_V4,
    },
    {
      name: 'targetIp',
      type: isVnfCidr ? VALIDATION.IPS_CIDR : VALIDATION.IP_V4,
    },
  ];

  useEffect(() => {
    actions.dataCenter.get({ id: dcId });
  }, [dcId]);

  const onSave = (values) => {
    if (id) {
      delete values.id;
      actions.nic.firewallRuleUpdate({
        dcId, nicId, properties: values, serverId, ruleId: id,
      });
    } else {
      cleanEmptyProperty(values);
      actions.nic.firewallRuleAdd({
        dcId, nicId, properties: values, serverId,
      });
    }
  };

  const rulesApplyHandler = async () => {
    for (const rule of rules) {
      actions.nic.firewallRuleAdd({
        dcId, nicId, properties: rule, serverId,
      });
      await sleep(1000);
    }
  };

  const getProperties = (e) => [
    ['Name', 'Ports', 'Protocol'],
    ...e.map(({ name, portRangeStart, portRangeEnd }) => [name, `${portRangeStart}-${portRangeEnd}`, 'TCP']),
  ];

  return (
    <>
      <Shared.forms.Form
        fields={fields}
        fieldsValues={fieldsValues}
        onProps={fieldsValues}
        onSubmit={onSave}
        isCancel={isCancel}
        onCancel={onCancel}
        validations={validations}
        changeFields={changeFields}
      />

      <Shared.dialog.PropertiesDialog
        show={!!rules && !!rules.length}
        onClose={() => setRules([])}
        title="Firewall rules to be added"
        properties={rules ? getProperties(rules) : []}
        onCancel={() => setRules([])}
        onApply={rulesApplyHandler}
      />
    </>
  );
};
